import { PortalUser } from '@/common/components/ecorr-store/portal-user/portal-user';
import { ParadataEventEnum, ParadataEventService } from '@/common/paradata-event/paradata-event.service';
import { FileUtilService } from '@/common/utils/file-util.service';
import { AddCommentDialogHttpService } from '@/support/dialogs/services';
import { Component, HostListener, Inject, OnDestroy, OnInit, inject, signal, computed } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { lastValueFrom } from 'rxjs';
import { ApproveCommentDialogData } from './approve-comment-dialog-data';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { LinearLoadingComponent } from '@/common/components/linear-loading/linear-loading.component';

@Component({
    selector: 'approve-comment-dialog',
    templateUrl: './approve-comment-dialog.component.html',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatDialogModule,
        MatInputModule,
        LinearLoadingComponent,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatIconModule,
        MatCardModule
    ]
})
export class ApproveCommentDialogComponent implements OnInit, OnDestroy {
    // Services using inject()
    private addCommentDialogHttpService = inject(AddCommentDialogHttpService);
    private matDialogRef = inject(MatDialogRef<ApproveCommentDialogComponent>);
    private fileUtilService = inject(FileUtilService);
    private paradataEventService = inject(ParadataEventService);

    // Signals for reactive state
    loading = signal(false);
    submitting = signal(false);
    success = signal(false);
    errorMessage = signal('');

    // Computed signals
    isLoading = computed(() => this.loading());
    isSubmitting = computed(() => this.submitting());
    isSuccess = computed(() => this.success());
    hasError = computed(() => this.errorMessage().length > 0);

    // Other properties
    fullCaseID: string;
    user: PortalUser;
    comment: string;
    action: string;
    requestEmail: string;
    requestID: string;

    formGroup = new FormGroup({
        comment: new FormControl('', [])
    });

    constructor(
        @Inject(MAT_DIALOG_DATA) public approveCommentDialogData: ApproveCommentDialogData
    ) {
    }

    async uploadFiles() {
        const formParam: any = {};
        formParam.hasApproval = (this.action === 'APPROVAL');
        formParam.surveyDId = this.requestID;
        formParam.comment = this.formGroup.get('comment').value;
        if (!formParam.comment || formParam.comment == null) {
            formParam.comment = '';
        }
        formParam.submittedByEmail = this.user.email;
        formParam.submittedByFirstName = this.user.firstName;
        formParam.submittedByLastName = this.user.lastName;

        this.loading.set(true);
        this.submitting.set(true);
        this.success.set(false);
        this.paradataEventService.add(ParadataEventEnum.Caseswithattachmentsfromcomments);
        await lastValueFrom(this.addCommentDialogHttpService.uploadFiles(formParam, new FormData(), this.fullCaseID))
            .then(
                response => {
                    if (response.status === 200) {
                        this.success.set(true);
                        setTimeout(() => {
                            this.confirm();
                        }, 3000);
                    }
                },
                error => {
                    console.error('approve-comment', error);
                    this.errorMessage.set('An error occurred while processing the request');
                }
            ).finally(
                () => {
                    this.loading.set(false);
                    this.submitting.set(false);
                }
            );
    }

    ngOnInit() {
        this.loading.set(true);
        this.fullCaseID = this.approveCommentDialogData.fullCaseID;
        this.user = this.approveCommentDialogData.user;
        this.requestEmail = this.approveCommentDialogData.requestEmail;
        this.requestID = this.approveCommentDialogData.requestID;
        this.action = this.approveCommentDialogData.action;
    }

    getFileIcon(name: string) {
        return this.fileUtilService.getFileIcon(name);
    }

    confirm() {
        this.close('confirm');
    }

    cancel() {
        this.close('cancel');
    }

    close(value: any) {
        this.matDialogRef.close(value);
    }

    onSubmit() {
        this.loading.set(true);
        this.uploadFiles();
    }

    @HostListener('keydown.esc')
    public onEsc() {
        this.close('ESC keydown');
    }

    ngOnDestroy() {
        this.paradataEventService.flushParadataevents();
    }
}
